import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Package, TrendingUp, DollarSign, Award } from 'lucide-react';

/**
 * Componente de gráfico de rendimiento por producto
 * Muestra métricas de producción, conversión y revenue por producto
 */
const ProductPerformanceChart = ({ 
  data, 
  isLoading = false,
  className = "",
  showRevenue = true,
  showConversion = true
}) => {
  /* Multi-series: paleta de 8 colores Yool, máx contraste */
  const COLORS = [
    'var(--fy-teal)',    /* 1 brand/contrataciones */
    'var(--fy-sky)',     /* 2 informativo          */
    'var(--fy-violet)',  /* 3 premium              */
    'var(--fy-orange)',  /* 4 advertencia          */
    'var(--fy-purple)',  /* 5 campañas             */
    'var(--fy-indigo)',  /* 6 conversiones         */
    'var(--fy-cyan)',    /* 7 analíticas           */
    'var(--fy-pink)',    /* 8 alerta urgente       */
  ];

  const formatData = (productData) => {
    if (!productData || !Array.isArray(productData)) return [];

    return productData.map((product, index) => ({
      ...product,
      color: COLORS[index % COLORS.length],
      conversionRate: product.conversionRate || 0,
      totalRevenueUF: product.totalRevenueUF || 0
    }));
  };

  const chartData = formatData(data);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-semibold mb-2">{data.productName}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: <span className="font-medium">
                {entry.name.includes('UF') ? 
                  `${entry.value.toLocaleString()} UF` : 
                  entry.value.toLocaleString()
                }
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderProductionChart = () => (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis 
            dataKey="productName" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="totalQuotations" name="Cotizaciones" fill="var(--fy-sky)" radius={[2, 2, 0, 0]} />
          <Bar dataKey="totalContractions" name="Contrataciones" fill="var(--fy-teal)" radius={[2, 2, 0, 0]} />
          <Bar dataKey="totalCertificates" name="Certificados" fill="var(--fy-teal)" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderRevenueChart = () => (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis 
            dataKey="productName" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="totalRevenueUF" name="Revenue Total (UF)" fill="var(--fy-sky)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderConversionChart = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Gráfico de barras de conversión */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="productName" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-background border rounded-lg shadow-lg p-3">
                      <p className="font-semibold mb-2">{label}</p>
                      <p className="text-sm">
                        Tasa de Conversión: <span className="font-medium">{payload[0].value.toFixed(1)}%</span>
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="conversionRate" name="Tasa de Conversión (%)" fill="var(--fy-indigo)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Gráfico de pie de distribución */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ productName, conversionRate }) => `${productName}: ${conversionRate.toFixed(1)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="conversionRate"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-background border rounded-lg shadow-lg p-3">
                      <p className="font-semibold">{data.productName}</p>
                      <p className="text-sm">Conversión: {data.conversionRate.toFixed(1)}%</p>
                    </div>
                  );
                }
                return null;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const renderProductStats = () => {
    if (!chartData || chartData.length === 0) return null;

    const totalQuotations = chartData.reduce((sum, p) => sum + (p.totalQuotations || 0), 0);
    const totalContractions = chartData.reduce((sum, p) => sum + (p.totalContractions || 0), 0);
    const totalRevenue = chartData.reduce((sum, p) => sum + (p.totalRevenueUF || 0), 0);
    const avgConversion = chartData.reduce((sum, p) => sum + (p.conversionRate || 0), 0) / chartData.length;

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="flex items-center space-x-3 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
          <Package className="h-8 w-8 text-blue-600" />
          <div>
            <p className="text-sm font-medium text-muted-foreground">Total Productos</p>
            <p className="text-2xl font-bold text-foreground">{chartData.length}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
          <TrendingUp className="h-8 w-8 text-[var(--yool-success)]" />
          <div>
            <p className="text-sm font-medium text-muted-foreground">Contrataciones</p>
            <p className="text-2xl font-bold text-foreground">{totalContractions.toLocaleString()}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
          <DollarSign className="h-8 w-8 text-purple-600" />
          <div>
            <p className="text-sm font-medium text-muted-foreground">Revenue Total</p>
            <p className="text-2xl font-bold text-foreground">{totalRevenue.toLocaleString()} UF</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
          <Award className="h-8 w-8 text-orange-600" />
          <div>
            <p className="text-sm font-medium text-muted-foreground">Conversión Promedio</p>
            <p className="text-2xl font-bold text-foreground">{avgConversion.toFixed(1)}%</p>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5" />
            <span>Rendimiento por Producto</span>
          </CardTitle>
          <CardDescription>
            Métricas de producción y conversión por producto
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-96 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5" />
            <span>Rendimiento por Producto</span>
          </CardTitle>
          <CardDescription>
            Métricas de producción y conversión por producto
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-96 flex items-center justify-center text-muted-foreground">
            No hay datos de productos disponibles
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Package className="h-5 w-5" />
          <span>Rendimiento por Producto</span>
        </CardTitle>
        <CardDescription>
          Métricas de producción y conversión por producto
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Estadísticas generales */}
          {renderProductStats()}

          {/* Tabs con diferentes vistas */}
          <Tabs defaultValue="production" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="production">Producción</TabsTrigger>
              {showRevenue && <TabsTrigger value="revenue">Revenue</TabsTrigger>}
              {showConversion && <TabsTrigger value="conversion">Conversión</TabsTrigger>}
            </TabsList>
            
            <TabsContent value="production" className="space-y-4">
              <div className="text-sm text-muted-foreground mb-4">
                Comparación de cotizaciones, contrataciones y certificados por producto
              </div>
              {renderProductionChart()}
            </TabsContent>
            
            {showRevenue && (
              <TabsContent value="revenue" className="space-y-4">
                <div className="text-sm text-muted-foreground mb-4">
                  Revenue total generado por cada producto (en UF)
                </div>
                {renderRevenueChart()}
              </TabsContent>
            )}
            
            {showConversion && (
              <TabsContent value="conversion" className="space-y-4">
                <div className="text-sm text-muted-foreground mb-4">
                  Tasas de conversión y distribución por producto
                </div>
                {renderConversionChart()}
              </TabsContent>
            )}
          </Tabs>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductPerformanceChart;